import { EventBus } from "jiffy-store";
import events from "common/events";
import { track } from "jiffy-analytics";
import { fetchPdpDeliveryEstimates } from "common/utils/delivery-date/fetch-pdp-delivery-estimates";
import { formatDeliveryDateForDisplay } from "common/utils/format/date";

const DTF_PRODUCT_SLUG = "jiffytransfers-DTF001";
const DTF_COMPOSITION_NAME = "Transfer";

const SELECTORS = {
  CONTAINER: "js-product-details-bogo-transfers-banner",
  DELIVERY_DATE: ".js-bogo-delivery-date",
  BLANKS_PENDING: ".js-bogo-blanks-pending",
  BLANKS_DONE: ".js-bogo-blanks-done",
  DONE_BADGE: ".js-bogo-done-badge",
  SIGNUP_LINK: ".js-bogo-signup-link"
};

const $getContainer = () => document.getElementById(SELECTORS.CONTAINER);
const $getDeliveryDate = () => document.querySelector(SELECTORS.DELIVERY_DATE);
const $getBlanksPending = () => document.querySelector(SELECTORS.BLANKS_PENDING);
const $getBlanksDone = () => document.querySelector(SELECTORS.BLANKS_DONE);
const $getDoneBadge = () => document.querySelector(SELECTORS.DONE_BADGE);
const $getSignupLink = () => document.querySelector(SELECTORS.SIGNUP_LINK);

let cachedDeliveryDate = null;
let blanksAddedThisSession = false;

/**
 * Fetches DTF transfer delivery date from API
 * @returns {Promise<string|null>} The formatted delivery date or null
 */
const fetchDtfDeliveryDate = async () => {
  if (cachedDeliveryDate) {
    return cachedDeliveryDate;
  }

  try {
    const payload = {
      composition_name: DTF_COMPOSITION_NAME,
      variants_hash: JSON.stringify({}),
      pre_cut: false,
      dtf_print: [{ height: 1, width: 1, quantity: 1 }]
    };

    const response = await fetchPdpDeliveryEstimates({ productId: DTF_PRODUCT_SLUG }, payload);

    if (response?.composition_dates?.value_full) {
      cachedDeliveryDate = response.composition_dates.value_full;
      return cachedDeliveryDate;
    }

    return null;
  } catch (error) {
    console.warn("Failed to fetch DTF delivery date:", error);
    return null;
  }
};

/**
 * Updates the delivery date display in the BOGO banner
 * @param {string} deliveryDate - The formatted delivery date
 */
const updateDeliveryDateDisplay = deliveryDate => {
  const $deliveryDate = $getDeliveryDate();
  if (!$deliveryDate || !deliveryDate) return;

  $deliveryDate.textContent = formatDeliveryDateForDisplay(deliveryDate);
};

/**
 * Updates the blanks section based on cart state
 * @param {boolean} hasBlanksInCart - Whether cart contains blanks
 */
const updateBlanksState = hasBlanksInCart => {
  const $blanksPending = $getBlanksPending();
  const $blanksDone = $getBlanksDone();
  const $doneBadge = $getDoneBadge();

  if (hasBlanksInCart) {
    $blanksPending?.classList.add("hidden");
    $blanksDone?.classList.remove("hidden");
    $doneBadge?.classList.remove("hidden");
  } else {
    $blanksPending?.classList.remove("hidden");
    $blanksDone?.classList.add("hidden");
    $doneBadge?.classList.add("hidden");
  }
};

/**
 * Builds cart_contents array based on what's in the cart
 * @returns {string[]} Array of cart item categories
 */
const getCartContents = () => {
  const contents = [];
  if (document.pdpDynamicData?.has_blanks_in_cart || blanksAddedThisSession) {
    contents.push("Blanks");
  }
  if (document.pdpDynamicData?.has_transfers_in_cart) {
    contents.push("Transfers");
  }
  return contents;
};

/**
 * Handles click on signup link and tracks mixpanel event
 */
const initSignupLinkTracking = () => {
  $getSignupLink()?.addEventListener("click", () => {
    track("click_discount_signup_link", {
      current_page: "PDP",
      cart_contents: getCartContents()
    });
  });
};

/**
 * Initializes the blanks state from dynamic data
 */
const initBlanksState = () => {
  const hasBlanksInCart = document.pdpDynamicData?.has_blanks_in_cart || false;
  updateBlanksState(hasBlanksInCart);
};

/**
 * Initializes delivery date fetch
 */
const initDeliveryDate = async () => {
  const deliveryDate = await fetchDtfDeliveryDate();
  if (deliveryDate) {
    updateDeliveryDateDisplay(deliveryDate);
  }
};

/**
 * Handles items added to cart event - update blanks state to "done"
 */
const handleItemsAddedToCart = () => {
  // Since we're on blanks PDP, items added are blanks
  blanksAddedThisSession = true;
  updateBlanksState(true);
};

/**
 * Initializes the BOGO transfers banner component
 */
const init = () => {
  const $container = $getContainer();
  if (!$container) return;

  // Initialize blanks state as soon as dynamic data is available
  if (window.dynamicDataLoaded) {
    initBlanksState();
  } else {
    document.addEventListener("dynamicDataLoaded", initBlanksState, false);
  }

  // Listen for cart updates to instantly show done state when blanks are added
  EventBus.on(events.ITEMS_ADDED_TO_CART, handleItemsAddedToCart);

  // Fetch delivery date in parallel (don't block blanks state)
  initDeliveryDate();

  // Initialize signup link click tracking for mixpanel
  initSignupLinkTracking();
};

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

export default init;
